import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  savePayRangeDraft,
  suggestPayRange,
  uploadPayRangeSourceDocument,
} from "@/api/payRangeAPI";
import { toListRowFromForm } from "@components/PayRangeModal/payRangeDraftPayload";

/** Aborted streams (wizard closed / Try Again) must not surface as a store error. */
function isAbortError(err) {
  return (
    err?.name === "AbortError" || err?.name === "CanceledError" || err?.code === "ERR_CANCELED"
  );
}

export const usePayRangeStore = defineStore("payRange", () => {
  const payRanges = ref([]);
  const saving = ref(false);
  const uploading = ref(false);
  const error = ref(null);

  const getPayRanges = computed(() => payRanges.value);
  const isSaving = computed(() => saving.value);
  const isUploading = computed(() => uploading.value);
  const getError = computed(() => error.value);

  function setPayRanges(rows) {
    payRanges.value = rows || [];
  }

  function clearError() {
    error.value = null;
  }

  /**
   * Persist create/edit draft via API and sync local list rows.
   * @param {import('@components/PayRangeModal/payRangeDraftPayload').PayRangeDraftFormPayload} form
   */
  async function saveDraft(form) {
    saving.value = true;
    error.value = null;
    try {
      const response = await savePayRangeDraft(form);
      const savedId = response?.data?.id || form.payRangeId || `draft-${Date.now()}`;
      const row = toListRowFromForm(form, {
        id: savedId,
        order: payRanges.value.length + 1,
      });

      if (form.mode === "edit" && form.payRangeId) {
        const index = payRanges.value.findIndex((r) => r.id === form.payRangeId);
        if (index >= 0) {
          row.order = payRanges.value[index].order;
          row.used = payRanges.value[index].used;
          payRanges.value.splice(index, 1, row);
        } else {
          payRanges.value.unshift(row);
        }
      } else {
        payRanges.value.unshift(row);
      }

      return response;
    } catch (err) {
      error.value = err;
      throw err;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Upload optional PDF source document for a draft.
   * @param {File} file
   * @param {{ payRangeId?: string|null }} [options]
   */
  async function uploadSourceDocument(file, options = {}) {
    uploading.value = true;
    error.value = null;
    try {
      return await uploadPayRangeSourceDocument(file, options);
    } catch (err) {
      error.value = err;
      throw err;
    } finally {
      uploading.value = false;
    }
  }

  /**
   * Retrieve market data for the Suggest Range panel (streamed lifecycle).
   *
   * Forwards the stream callbacks + abort signal to the API so the panel can
   * render phase / narrative / stalled while the terminal result is pending.
   *
   * @param {object} seed
   * @param {{
   *   onThinking?: (text: string) => void,
   *   onPhase?: (phase: string) => void,
   *   onStalled?: () => void,
   *   signal?: AbortSignal,
   * }} [options]
   */
  async function suggestRange(seed, options = {}) {
    error.value = null;
    try {
      return await suggestPayRange(seed, options);
    } catch (err) {
      // An abort is the user leaving / retrying, not a failed suggestion.
      if (!isAbortError(err)) error.value = err;
      throw err;
    }
  }

  function reorderPayRanges(oldIndex, newIndex) {
    if (oldIndex === undefined || newIndex === undefined) return;
    const item = payRanges.value.splice(oldIndex, 1)[0];
    payRanges.value.splice(newIndex, 0, item);
    payRanges.value.forEach((row, index) => {
      row.order = index + 1;
    });
  }

  return {
    payRanges,
    saving,
    uploading,
    error,
    getPayRanges,
    isSaving,
    isUploading,
    getError,
    setPayRanges,
    clearError,
    saveDraft,
    uploadSourceDocument,
    suggestRange,
    reorderPayRanges,
  };
});
